import {useEffect, useRef, useState} from 'react'

type Props<
 T extends {label: string; value: string | number}
> = {
 label: string
 value?: T | null
 onChange: (val: T) => void
 options: T[]
 placeholder?: string
 required?: boolean
 disabled?: boolean
}

function CustomDropdown<
 T extends {label: string; value: string | number}
>({
 label,
 value,
 onChange,
 options,
 placeholder,
 required,
 disabled,
}: Props<T>) {
 const [open, setOpen] = useState(false)
 const ref = useRef<HTMLDivElement>(null)

 const displayLabel =
  value?.label || placeholder || 'Select'

 useEffect(() => {
  const handler = (e: MouseEvent) => {
   if (
    ref.current &&
    !ref.current.contains(e.target as Node)
   ) {
    setOpen(false)
   }
  }
  document.addEventListener('mousedown', handler)
  return () =>
   document.removeEventListener('mousedown', handler)
 }, [])

 return (
  <div ref={ref} className='relative'>
   <div
    className={
     `rounded-xl border px-4 py-3 focus-within:border-black ` +
     (value ? 'bg-[#F9FAFB]' : 'bg-white')
    }
   >
    <p className='text-xs text-[#3F76FF] font-medium'>
     {label}
     {required && <span className='text-red-500'>*</span>}
    </p>

    <button
     type='button'
     onClick={() => {
      if (!disabled) {
       setOpen(!open)
      }
     }}
     className='mt-1 flex w-full items-center justify-between bg-transparent text-left outline-none'
    >
     <span
      className={`text-lg ${
       value ? 'text-gray-900' : 'text-gray-400'
      }`}
     >
      {displayLabel}
     </span>

     <svg
      className={`h-4 w-4 text-gray-600 transition-transform ${
       open && !disabled ? 'rotate-180' : ''
      }`}
      viewBox='0 0 20 20'
      fill='none'
      stroke='currentColor'
     >
      <path d='M6 8l4 4 4-4' strokeWidth='2' />
     </svg>
    </button>
   </div>

   {open && !disabled && (
    <div className='absolute z-20 mt-2 w-full overflow-hidden rounded-xl bg-[#F5F8FA] shadow-lg max-h-60 overflow-y-auto'>
     {options.map((opt, index) => (
      <div
       key={opt.value}
       onClick={() => {
        onChange(opt)
        setOpen(false)
       }}
       className={`flex cursor-pointer items-center gap-4 px-5 py-5 hover:bg-[#EEF3F7] ${
        index !== options.length - 1
         ? 'border-b border-[#E6EBF0]'
         : ''
       }`}
      >
       <div className='flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#3F76FF]'>
        {value?.value === opt.value && (
         <div className='h-3 w-3 rounded-full bg-[#3F76FF]' />
        )}
       </div>

       <span className='font-medium text-gray-800'>
        {opt.label}
       </span>
      </div>
     ))}
    </div>
   )}
  </div>
 )
}

export default CustomDropdown
